import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Button,
    FileUploader,
    DownloadTemplate
} from 'common/components';
import { ButtonVariant } from 'common/enums';
import {
    ACTION_ITEMS,
    CORE_FILE_UPLOAD_TITLE,
    FILE_COUNT_ERROR,
    FILE_COUNT_MAX,
    FILE_TYPE_ERROR
} from 'common/config';
import { FileDetails } from 'common/models';
import { isAllowedFileType } from 'common/utils/file';
import { RootState } from 'common/redux/core/root.reducer';
import { getSelectedFiles, getUploadingFiles } from 'common/redux/file-upload/fileUpload.selector';
import { fileUploadActions } from 'common/redux/file-upload';

import './UploadModal.scss';

type UploadModalProps = {
    isVisible: boolean;
    targetId?: string;
};

const UploadModal: FC<UploadModalProps> = ({ isVisible, targetId }) => {
    const [error, setError] = useState<string>('');
    const selectedFiles = useSelector((state: RootState) => getSelectedFiles(state));
    const uploadingFiles = useSelector((state: RootState) => getUploadingFiles(state));

    const dispatch = useDispatch();

    const handleClose = () => {
        setError('');
        dispatch(fileUploadActions.setVisibility(false));
    };

    const handleSelectedFileRemove = (file: FileDetails) => () => {
        const updatedFiles = selectedFiles
            ? selectedFiles.filter((item: FileDetails) => file.name !== item.name)
            : [];
        dispatch(fileUploadActions.setSelectedFiles(updatedFiles));
    };

    const handleDrop = (files: Array<FileDetails>) => {
        let isAllowedType = true;

        setError('');
        dispatch(fileUploadActions.setSelectedFiles([]));

        if (files.length > FILE_COUNT_MAX) {
            setError(FILE_COUNT_ERROR);
        } else {
            const newFiles = files.map((item: FileDetails) => {
                item.targetID = targetId;
                isAllowedType = isAllowedFileType(item, /(\.xls|\.xlsx)$/i);
                return item;
            });
            if (isAllowedType) {
                dispatch(fileUploadActions.setSelectedFiles(newFiles));
            } else {
                setError(FILE_TYPE_ERROR);
            }
        }
    };

    const handleUpload = () => {
        setError('');
        dispatch(
            fileUploadActions.uploadFile({
                files: selectedFiles,
                targetId: targetId ?? '',
                variantName: null,
                entityType: ''
            })
        );
    };

    return (
        <Modal style={{ width: '51vw' }} show={isVisible} onClose={handleClose}>
            <ModalHeader className="upload-modal-header">{CORE_FILE_UPLOAD_TITLE}</ModalHeader>
            <ModalBody className="upload-modal-body">
                <DownloadTemplate />
                <FileUploader
                    maxFiles={FILE_COUNT_MAX}
                    uploadingFiles={uploadingFiles}
                    selectedFiles={selectedFiles}
                    errorMessage={error}
                    onSelectedFileRemove={handleSelectedFileRemove}
                    onDrop={handleDrop}
                />
            </ModalBody>
            <ModalFooter className="upload-modal-footer">
                <Button
                    variant={ButtonVariant.Primary}
                    disabled={selectedFiles?.length < 1}
                    onClick={handleUpload}>
                    {ACTION_ITEMS.UPLOAD_BUTTON}
                </Button>
                <Button variant={ButtonVariant.Secondary} onClick={handleClose}>
                    {ACTION_ITEMS.CANCEL_BUTTON}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default UploadModal;
